import React, { useState, useRef, useCallback } from "react";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";

interface ResizableColumnHeaderProps {
  id: string;
  title: string;
  width: number;
  sortDirection?: "asc" | "desc" | null;
  onSort: () => void;
  onResize: (newWidth: number) => void;
  onAutoResize: () => void;
  isDragDisabled?: boolean;
}

export const ResizableColumnHeader: React.FC<ResizableColumnHeaderProps> = ({
  id,
  title,
  width,
  sortDirection,
  onSort,
  onResize,
  onAutoResize,
  isDragDisabled = false,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: isDragDisabled || isResizing,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    const localStartX = e.clientX;
    const localStartWidth = width;

    const handleMouseMove = (ev: MouseEvent) => {
      const diff = ev.clientX - localStartX;
      const adjustedDiff = diff * 0.5;
      const newWidth = Math.max(50, localStartWidth + adjustedDiff);
      onResize(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [width, onResize]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onAutoResize();
  }, [onAutoResize]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center justify-center h-full transition-colors flex-1 min-w-0 relative group overflow-hidden",
        isDragging && "opacity-50 z-50"
      )}
    >
      {/* Drag Handle */}
      {!isDragDisabled && (
        <div
          {...attributes}
          {...listeners}
          className="absolute left-1 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-10"
        >
          <GripVertical className="h-3 w-3 text-gray-600" />
        </div>
      )}

      {/* Header Content */}
      <div className="flex items-center justify-center w-full h-full px-3 overflow-hidden min-w-0">
        <div 
          className="flex items-center justify-center flex-1 h-full cursor-pointer overflow-hidden min-w-0"
          onClick={onSort}
        >
          <span className="text-gray-900 font-semibold text-sm leading-none text-center truncate max-w-full">
            {title}
          </span>
        </div>
      </div>
    </div>
  );
};